import { fetchSiteConnections } from './api';
import registry from '../../../sites.json';

/** A site's id, as listed in sites.json at the repository root. */
export type SiteId = string;

export interface SiteConfig {
  id: SiteId;
  name: string;
  /** Where the site is served: empty for the main site, otherwise such as "/myunfi". */
  path: string;
  homeSlug: string;
  logo: { light: string; dark: string };
  /** The build's Auth0 connection for Log In, used when none is set under Sign-in. */
  connection?: string;
}

const env = import.meta.env as Record<string, string | undefined>;

/** Every site, in the order sites.json lists them; the backend reads the same file. */
export const SITE_LIST: SiteConfig[] = registry.sites.map((site) => ({
  id: site.id,
  name: site.name,
  path: site.path,
  homeSlug: site.home,
  logo: site.logo,
  connection: env[`VITE_AUTH0_CONNECTION_${site.id.toUpperCase()}`] || undefined,
}));

/** The main site, served at the root and used when no other site matches. */
export const DEFAULT_SITE: SiteConfig = SITE_LIST[0];

export function siteById(id: SiteId): SiteConfig | undefined {
  return SITE_LIST.find((site) => site.id === id);
}

export function resolveSite(pathname: string): SiteConfig {
  return (
    SITE_LIST.find(
      (site) => site.path && (pathname === site.path || pathname.startsWith(`${site.path}/`))
    ) || DEFAULT_SITE
  );
}

/** The Auth0 connection a site's Log In uses: the one set under Sign-in, else the build's. */
export async function loginConnection(site: SiteConfig): Promise<string | undefined> {
  try {
    return (await fetchSiteConnections())[site.id] || site.connection;
  } catch {
    return site.connection;
  }
}

export function slugFromSitePath(pathname: string, site: SiteConfig): string {
  const relative = site.path ? pathname.slice(site.path.length) : pathname;
  return relative.replace(/^\/+|\/+$/g, '') || site.homeSlug;
}

export function sitePathForSlug(site: SiteConfig, slug: string): string {
  if (!slug || slug === site.homeSlug || (site.id === DEFAULT_SITE.id && slug === 'index')) {
    return site.path || '/';
  }
  return `${site.path}/${slug}`.replace(/\/+/g, '/');
}
